import { isDefined, isNotDefined } from '@sgme/fp';
import { Button, HStack } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import clsx from 'clsx';
import deepEqual from 'fast-deep-equal';
import { useRef, useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import { selectDataByCurrencyPair } from '@/api/config/selectors.ts';
import { useCheckSwapByTradeCapture } from '@/App/tiles/Swap/SwapContext.tsx';
import { InputErrorTooltip } from '@/components/ui/InputErrorTooltip.tsx';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { selectSwapPrices } from '@/store/swap/selectors/rfs.ts';
import { selectSwapTile } from '@/store/swap/selectors/tile.ts';
import { swapCommercialMarginSpotPointsUpdated } from '@/store/swap/slice.ts';
import type { BidAsk } from '@/types/common.ts';
import { parseBidAskPair } from '@/utils/bidAskPairEdition.ts';
import { roundToFixed } from '@/utils/number.ts';

type SwapDetailPricesProps = {
  tileId: string;
};

export const SwapDetailPrices = (props: SwapDetailPricesProps) => {
  const { tileId } = props;

  const { sgwtWebAnalytics } = useSgwtWidgets();

  const dispatch = useAppDispatch();

  const checkSwapWithTradeCapture = useCheckSwapByTradeCapture();

  const {
    currencyPairPrecision,
    prices,
    pinnedNearPrice,
    pinnedFarPrice,
    isNonDeliverable,
    isHistoricalRate,
    tradeCapture,
  } = useAppSelector((state) => {
    const tile = selectSwapTile(state, tileId);

    if (isNotDefined(tile)) {
      return {
        isNonDeliverable: false,
        isHistoricalRate: false,
      };
    }

    const dataByCurrencyPair = selectDataByCurrencyPair(state);
    const currencyPair = tile.properties.currencyPair;

    if (isNotDefined(currencyPair) || isNotDefined(dataByCurrencyPair.data)) {
      return {
        isNonDeliverable: false,
        isHistoricalRate: false,
      };
    }

    const currencyPairData = dataByCurrencyPair.data.dict[currencyPair];

    if (isNotDefined(currencyPairData)) {
      return {
        isNonDeliverable: false,
        isHistoricalRate: false,
      };
    }

    const currencyPairPrecision = currencyPairData.precision;

    return {
      currencyPairPrecision,
      prices: selectSwapPrices(state, tileId),
      pinnedNearPrice: tile.properties.nearPrice,
      pinnedFarPrice: tile.isNonDeliverable ? undefined : tile.properties.farPrice,
      isNonDeliverable: tile.isNonDeliverable,
      isHistoricalRate: tile.isNonDeliverable ? false : tile.properties.type === 'historical',
      tradeCapture: tile.tradeCapture,
    };
  }, deepEqual);

  // ███╗   ██╗███████╗ █████╗ ██████╗     ██████╗ ██████╗ ██╗ ██████╗███████╗
  // ████╗  ██║██╔════╝██╔══██╗██╔══██╗    ██╔══██╗██╔══██╗██║██╔════╝██╔════╝
  // ██╔██╗ ██║█████╗  ███████║██████╔╝    ██████╔╝██████╔╝██║██║     █████╗
  // ██║╚██╗██║██╔══╝  ██╔══██║██╔══██╗    ██╔═══╝ ██╔══██╗██║██║     ██╔══╝
  // ██║ ╚████║███████╗██║  ██║██║  ██║    ██║     ██║  ██║██║╚██████╗███████╗
  // ╚═╝  ╚═══╝╚══════╝╚═╝  ╚═╝╚═╝  ╚═╝    ╚═╝     ╚═╝  ╚═╝╚═╝ ╚═════╝╚══════╝

  /**
   * states:
   * - hidden
   * - standard stream values visible
   * - pinned values visible
   * - input visible (edited)
   */
  // the edited price. if undefined, the input is hidden
  const [editedNearPrice, setEditedNearPrice] = useState<string | undefined>(undefined);
  const [editedNearPriceInError, setEditedNearPriceInError] = useState(false);

  const [isEditedNearPriceDirty, setIsNearPriceDirty] = useState(false); // if the user doesn't change the price (dirty = false), don't call tradecapture
  const nearPriceInput = useRef<HTMLInputElement>(null);

  const isNearPriceEditable =
    (isHistoricalRate && isNotDefined(prices)) ||
    (isDefined(prices) && ((isHistoricalRate && isDefined(pinnedNearPrice)) || !isHistoricalRate));

  const nearPriceStatus = isDefined(editedNearPrice)
    ? 'edited'
    : (isHistoricalRate && isNotDefined(prices)) || isDefined(prices)
      ? isNearPriceEditable
        ? 'standardPrice.editable'
        : 'standardPrice'
      : 'hidden';

  const handleEditNearPrice = () => {
    if (isDefined(prices?.nearPrice)) {
      setEditedNearPrice(
        `${roundToFixed(prices.nearPrice.bid, prices.currencyPairPrecision + 1)}/${roundToFixed(prices.nearPrice.ask, prices.currencyPairPrecision + 1)}`,
      );
    } else if (isDefined(pinnedNearPrice) && isDefined(currencyPairPrecision)) {
      setEditedNearPrice(
        `${roundToFixed(pinnedNearPrice.bid, currencyPairPrecision + 1)}/${roundToFixed(pinnedNearPrice.ask, currencyPairPrecision + 1)}`,
      );
    } else {
      // it can be the case when the user set historical rate
      // he has to define the near price or the far price
      // to request the price
      setEditedNearPrice('');
    }

    setIsNearPriceDirty(false);

    // stop the edition of the near price
    setEditedFarPrice(undefined);
    setIsEditedFarPriceDirty(false);

    setTimeout(() => {
      if (isDefined(nearPriceInput.current)) {
        nearPriceInput.current?.focus();
        nearPriceInput.current?.select();
      }
    }, 0);
  };

  const handleNearPriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditedNearPrice(event.target.value);
    setIsNearPriceDirty(true);
  };

  const handleNearPriceSaved = () => {
    // if the user didn't change the value
    // just stop the edition
    if (!isEditedNearPriceDirty) {
      setEditedNearPrice(undefined);
      return;
    }

    if (
      !isNearPriceEditable ||
      isNotDefined(editedNearPrice) ||
      isNotDefined(tradeCapture) ||
      (!isHistoricalRate && isNotDefined(prices))
    ) {
      setEditedNearPrice(undefined);
      return;
    }

    const nearPrice = parseBidAskPair(editedNearPrice, { numbersFlavor: 'float' });

    if (isNotDefined(nearPrice)) {
      setEditedNearPriceInError(true);
      return;
    }
    setEditedNearPriceInError(false);

    setEditedNearPrice(undefined);
    setIsNearPriceDirty(false);

    // stop the edition of the near price
    setEditedFarPrice(undefined);
    setIsEditedFarPriceDirty(false);

    // whenever pinned margin spot changes
    // the commercial margin must be reset to default margins provided by the quote
    sgwtWebAnalytics?.trackEvent('Swap', 'Commercial margin swap points reseted');

    dispatch(
      swapCommercialMarginSpotPointsUpdated({
        tileId,
        value: undefined,
      }),
    );

    sgwtWebAnalytics?.trackEvent('Swap', 'Near price changed', isDefined(nearPrice) ? String(nearPrice) : '');

    checkSwapWithTradeCapture({
      // if the user delete the field, erase the user value
      nearPrice: nearPrice ?? null,
    });
  };

  const handleUnpinnedNearPrice = () => {
    if (isNotDefined(pinnedNearPrice) || isNotDefined(isNonDeliverable) || isNotDefined(tradeCapture)) {
      return;
    }

    setIsNearPriceDirty(false);
    setEditedNearPrice(undefined);

    sgwtWebAnalytics?.trackEvent('Swap', 'Near price reseted');

    checkSwapWithTradeCapture({
      // if the user delete the field, erase the user value
      nearPrice: null,
    });
  };

  // ███████╗ █████╗ ██████╗     ██████╗ ██████╗ ██╗ ██████╗███████╗
  // ██╔════╝██╔══██╗██╔══██╗    ██╔══██╗██╔══██╗██║██╔════╝██╔════╝
  // █████╗  ███████║██████╔╝    ██████╔╝██████╔╝██║██║     █████╗
  // ██╔══╝  ██╔══██║██╔══██╗    ██╔═══╝ ██╔══██╗██║██║     ██╔══╝
  // ██║     ██║  ██║██║  ██║    ██║     ██║  ██║██║╚██████╗███████╗
  // ╚═╝     ╚═╝  ╚═╝╚═╝  ╚═╝    ╚═╝     ╚═╝  ╚═╝╚═╝ ╚═════╝╚══════╝

  const [editedFarPrice, setEditedFarPrice] = useState<string | undefined>(undefined);
  const [editedFarPriceInError, setEditedFarPriceInError] = useState(false);

  const [isEditedFarPriceDirty, setIsEditedFarPriceDirty] = useState(false); // if the user doesn't change the price (dirty = false), don't call tradecapture
  const farPriceInput = useRef<HTMLInputElement>(null);

  const isFarPriceEditable =
    isHistoricalRate && (isNotDefined(prices) || (isDefined(prices) && isDefined(pinnedFarPrice)));

  const farPriceStatus = isDefined(editedFarPrice)
    ? 'edited'
    : (isHistoricalRate && isNotDefined(prices)) || isDefined(prices)
      ? isFarPriceEditable
        ? 'standardPrice.editable'
        : 'standardPrice'
      : 'hidden';

  const handleEditFarPrice = () => {
    if (isDefined(prices?.farPrice)) {
      setEditedFarPrice(
        `${roundToFixed(prices.farPrice.bid, prices.currencyPairPrecision + 1)}/${roundToFixed(prices.farPrice.ask, prices.currencyPairPrecision + 1)}`,
      );
    } else if (isDefined(pinnedFarPrice) && isDefined(currencyPairPrecision)) {
      setEditedFarPrice(
        `${roundToFixed(pinnedFarPrice.bid, currencyPairPrecision + 1)}/${roundToFixed(pinnedFarPrice.ask, currencyPairPrecision + 1)}`,
      );
    } else {
      // it can be the case when the user set historical rate
      // he has to define the near price or the far price
      // to request the price
      setEditedFarPrice('');
    }

    setIsEditedFarPriceDirty(false);

    // stop the edition of the near price
    setEditedNearPrice(undefined);
    setIsNearPriceDirty(false);

    setTimeout(() => {
      if (isDefined(farPriceInput.current)) {
        farPriceInput.current?.focus();
        farPriceInput.current?.select();
      }
    }, 0);
  };

  const handleFarPriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditedFarPrice(event.target.value);
    setIsEditedFarPriceDirty(true);
  };

  const handleFarPriceSaved = () => {
    // if the user didn't change the value
    // just stop the edition
    if (!isEditedFarPriceDirty) {
      setEditedFarPrice(undefined);
      return;
    }

    if (
      isNonDeliverable ||
      !isFarPriceEditable ||
      isNotDefined(tradeCapture) ||
      isNotDefined(editedFarPrice) ||
      (!isHistoricalRate && isNotDefined(prices))
    ) {
      setEditedFarPrice(undefined);
      return;
    }

    const farPrice = parseBidAskPair(editedFarPrice, { numbersFlavor: 'float' });

    if (isNotDefined(farPrice)) {
      setEditedFarPriceInError(true);
      return;
    }
    setEditedFarPriceInError(false);

    setEditedFarPrice(undefined);
    setIsEditedFarPriceDirty(false);

    // stop the edition of the near price
    setEditedNearPrice(undefined);
    setIsNearPriceDirty(false);

    sgwtWebAnalytics?.trackEvent('Swap', 'Far price changed', isDefined(farPrice) ? String(farPrice) : '');

    checkSwapWithTradeCapture({
      // if the user delete the field, erase the user value
      farPrice: farPrice ?? null,
    });
  };

  const handleUnpinnedFarPrise = () => {
    if (isNonDeliverable || isNotDefined(pinnedFarPrice) || isNotDefined(tradeCapture)) {
      return;
    }

    setEditedFarPrice(undefined);

    sgwtWebAnalytics?.trackEvent('Swap', 'Far price reseted');

    checkSwapWithTradeCapture({
      // if the user delete the field, erase the user value
      farPrice: null,
    });
  };

  // ██████╗ ███████╗███╗   ██╗██████╗ ███████╗██████╗
  // ██╔══██╗██╔════╝████╗  ██║██╔══██╗██╔════╝██╔══██╗
  // ██████╔╝█████╗  ██╔██╗ ██║██║  ██║█████╗  ██████╔╝
  // ██╔══██╗██╔══╝  ██║╚██╗██║██║  ██║██╔══╝  ██╔══██╗
  // ██║  ██║███████╗██║ ╚████║██████╔╝███████╗██║  ██║
  // ╚═╝  ╚═╝╚══════╝╚═╝  ╚═══╝╚═════╝ ╚══════╝╚═╝  ╚═╝

  return (
    <div className="container fs-12 px-8px">
      <div className="row align-items-center no-gutters my-1">
        <div className="col-5 text-nowrap text-end">
          <FormattedMessage id="SwapTile.spot.label" />
        </div>
        <div className="col-7" data-e2e="spot-rates">
          {isDefined(prices) && <BidAskValueDisplay value={prices.spot} precision={prices.currencyPairPrecision} />}
        </div>
      </div>

      <div className="row align-items-center no-gutters my-1">
        <div className="col-5 text-nowrap text-end">
          <FormattedMessage id="SwapTile.nearPoints.label" />
        </div>
        <div className="col-7" data-e2e="near-points">
          {isDefined(prices) && <BidAskValueDisplay value={prices.nearPoints} precision={3} displayPlusSign />}
        </div>
      </div>

      <div className="row align-items-center no-gutters my-1">
        <div className="col-5 text-nowrap text-end">
          <FormattedMessage id="SwapTile.nearPrice.label" />
        </div>

        <HStack verticalAlignment="center" className="col-7 position-relative overflow-visible" gap="8px">
          {nearPriceStatus === 'edited' && (
            <InputErrorTooltip errorI18nKey={editedNearPriceInError ? 'tile.form.field.error.parsing' : undefined}>
              <input
                ref={nearPriceInput}
                value={editedNearPrice ?? ''}
                onChange={handleNearPriceChange}
                onBlur={handleNearPriceSaved}
                className={clsx('form-control form-control-sm', {
                  'text-info fw-bold': isDefined(pinnedNearPrice),
                  'is-invalid': editedNearPriceInError,
                })}
                data-e2e="near-price-input"
              />
            </InputErrorTooltip>
          )}

          {isDefined(currencyPairPrecision) &&
            (nearPriceStatus === 'standardPrice' || nearPriceStatus === 'standardPrice.editable') && (
              <HStack
                verticalAlignment="center"
                flex="expand"
                onClick={handleEditNearPrice}
                className={clsx({
                  'form-control form-control-sm': nearPriceStatus === 'standardPrice.editable',
                  'text-info fw-bold': isDefined(pinnedNearPrice),
                })}
                data-e2e="near-price"
              >
                <BidAskValueDisplay
                  value={prices?.nearPrice ?? pinnedNearPrice}
                  precision={nearPriceStatus === 'standardPrice.editable' ? 3 : currencyPairPrecision + 2}
                />
              </HStack>
            )}

          {isDefined(pinnedNearPrice) && (
            <Button
              iconPosition="icon-only"
              onClick={handleUnpinnedNearPrice}
              purpose="flat"
              size="sm"
              data-e2e="near-price-pin"
            >
              <i className="icon icon-sm">push_pin</i>
            </Button>
          )}
        </HStack>
      </div>

      <div className="row align-items-center no-gutters my-1">
        <div className="col-5 text-nowrap text-end">
          <FormattedMessage id="SwapTile.farPoints.label" />
        </div>
        <div className="col-7" data-e2e="far-points">
          {isDefined(prices) && <BidAskValueDisplay value={prices.farPoints} precision={3} displayPlusSign />}
        </div>
      </div>

      <div className="row align-items-center no-gutters my-1">
        <div className="col-5 text-nowrap text-end">
          <FormattedMessage id="SwapTile.farPrice.label" />
        </div>

        <HStack verticalAlignment="center" className="col-7 position-relative" gap="8px">
          {farPriceStatus === 'edited' && (
            <InputErrorTooltip errorI18nKey={editedFarPriceInError ? 'tile.form.field.error.parsing' : undefined}>
              <input
                ref={farPriceInput}
                value={editedFarPrice ?? ''}
                onChange={handleFarPriceChange}
                onBlur={handleFarPriceSaved}
                className={clsx('form-control form-control-sm', {
                  'text-info fw-bold': isDefined(pinnedFarPrice),
                  'is-invalid': editedFarPriceInError,
                })}
                data-e2e="far-price-input"
              />
            </InputErrorTooltip>
          )}

          {isDefined(currencyPairPrecision) &&
            (farPriceStatus === 'standardPrice' || farPriceStatus === 'standardPrice.editable') && (
              <HStack
                verticalAlignment="center"
                flex="expand"
                onClick={handleEditFarPrice}
                className={clsx({
                  'form-control form-control-sm': farPriceStatus === 'standardPrice.editable',
                  'text-info fw-bold': isDefined(pinnedFarPrice),
                })}
                data-e2e="far-price"
              >
                <BidAskValueDisplay
                  value={prices?.farPrice ?? pinnedFarPrice}
                  precision={farPriceStatus === 'standardPrice.editable' ? 3 : currencyPairPrecision + 2}
                />
              </HStack>
            )}

          {isDefined(pinnedFarPrice) && (
            <Button
              iconPosition="icon-only"
              onClick={handleUnpinnedFarPrise}
              purpose="flat"
              size="sm"
              data-e2e="far-price-pin"
            >
              <i className="icon icon-sm">push_pin</i>
            </Button>
          )}
        </HStack>
      </div>

      {isHistoricalRate && (
        <>
          <div className="row align-items-center no-gutters my-1">
            <div className="col-5 text-nowrap text-end">Far mkt points</div>

            <div className="col-7 position-relative" data-e2e="far-mkt-points">
              {isDefined(prices?.farMktPoints) && (
                <BidAskValueDisplay value={prices.farMktPoints} precision={3} displayPlusSign />
              )}
            </div>
          </div>

          <div className="row align-items-center no-gutters my-1">
            <div className="col-5 text-nowrap text-end">Treasury delta</div>

            <div className="col-7 position-relative" data-e2e="treasury-delta">
              {isDefined(prices?.treasuryDelta) && (
                <BidAskValueDisplay value={prices.treasuryDelta} precision={3} displayPlusSign />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

type BidAskValueDisplayProps = {
  value?: BidAsk<number>;
  precision: number;
  displayPlusSign?: boolean;
};

const BidAskValueDisplay = ({ value, precision, displayPlusSign = false }: BidAskValueDisplayProps) => {
  const signDisplay = displayPlusSign ? 'exceptZero' : 'auto';

  if (isNotDefined(value)) {
    return <span className="text-muted" />;
  }

  return (
    <>
      <FormattedNumber
        value={value.bid}
        minimumFractionDigits={precision}
        maximumFractionDigits={precision}
        signDisplay={signDisplay}
      />
      {' / '}
      <FormattedNumber
        value={value.ask}
        minimumFractionDigits={precision}
        maximumFractionDigits={precision}
        signDisplay={signDisplay}
      />
    </>
  );
};
